
import StripeSubscriptionService from '@/Services/Subscription/StripeSubscriptionService';
import { Team, User } from '@/classes/auth';
import { Invoice, LoadingState, Plan, Subscription } from '@/classes/stripe';
import store from '@/store';
import dayjs from 'dayjs';

import OptimizelyService from '@/Services/OptimizelyService';
import Alert from '@/components/Alert.vue';
import Icon from '@/components/Icons/Icon.vue';
import BillingAddressEdit from '@/components/Modals/Account/BillingAddressEdit.vue';
import CancelPauseSubscription from '@/components/Modals/Account/CancelPauseSubscription.vue';
import InvoiceEmailEdit from '@/components/Modals/Account/InvoiceEmailEdit.vue';
import ReactivateSubscription from '@/components/Modals/Account/ReactivateSubscription.vue';
import ResumeSubscription from '@/components/Modals/Account/ResumeSubscription.vue';
import VatIdEdit from '@/components/Modals/Account/VatIdEdit.vue';

import Invoices from '@/components/Stripe/Invoices.vue';
import PaymentMethodSummary from '@/components/Subscription/PaymentMethodSummary.vue';
import { Component, Vue } from 'vue-property-decorator';
import { isFeatureEnabled } from '@/Services/FeatureFlagService';
import { optimizelyFeatureFlags } from '@/plugins/optimizely-feature-flags';
import PromoCodeService from '@/Services/PromoCodeService';

@Component({
  components: {
    Invoices,
    BillingAddressEdit,
    VatIdEdit,
    CancelPauseSubscription,
    ReactivateSubscription,
    ResumeSubscription,
    InvoiceEmailEdit,
    Icon,
    Alert,
    PaymentMethodSummary,
  },
})
export default class activeSubscription extends Vue {
  team: Team = store.state.auth.team;
  // Modal states
  isDialogShown: any = {
    subscriptionCancelPause: false,
    addressEdit: false,
    vatIdEdit: false,
    invoiceEmailEdit: false,
    subscriptionResume: false,
    subscriptionReactivate: false,
  };

  isPaymentMode = false;

  legalEntityB2B: string = 'b2b';
  legalEntityB2C: string = 'b2c';

  tryAgainSubscription = false;
  errorSubscription = false;

  // Update state of update team request
  isTeamUpdating = false;

  isDiscountModalCouponActive = PromoCodeService.isCouponCodeEnabled();

  isFeatureEng2202Enabled = isFeatureEnabled(
    optimizelyFeatureFlags,
    'cancellation-discount-modal',
    store.state?.auth?.user?.id.toString()
  );

  // Handles payment method logic and some of the form state
  stripeSubscriptionService: StripeSubscriptionService = new StripeSubscriptionService();

  get activeSubscription(): Subscription {
    return store.state.subscription.activeSubscription;
  }

  get activePlan(): Plan {
    return store.state.subscription.activePlan;
  }

  get expiredSubscription(): boolean {
    if (!this.activeSubscription.ends_at) {
      return false;
    }
    return this.activeSubscription.ends_at.isBefore(dayjs());
  }

  get isDiscountBannerVisible(): boolean {
    return !this.activeSubscription.is_cancelled && this.isFeatureEng2202Enabled && this.isDiscountModalCouponActive;
  }

  /**
   * We check here if the current active plan is still upgradeable or not
   * by comparing against other plan levels.
   */
  get upgradeButton(): boolean {
    // !== 0 is there because if a user does not have a plan the ID will still be 0 due to the class setup
    return this.activePlan && this.activePlan.id !== 0;
  }

  get isCustomerB2BAndNeedsVatId(): boolean {
    return store.getters['location/isVatIdNeeded'] && this.team.legal_entity === 'b2b';
  }

  get showLoadingSubscription(): LoadingState {
    return store.state.subscription.showLoading;
  }

  get isDowngrading(): boolean {
    return this.stripeSubscriptionService.isPlanDowngrading(this.activeSubscription.plan_id);
  }

  get invoices(): Invoice[] {
    return store.state.account.invoices || [];
  }

  get hasInvoices(): boolean {
    return this.invoices?.length > 0 || false;
  }

  get newPlanName(): string {
    if (!this.activeSubscription.new_plan_id) return '';
    return this.stripeSubscriptionService.findPlan(this.activeSubscription.new_plan_id)?.title || '';
  }

  get user(): User {
    return store.state.auth.user;
  }

  openDialog(dialog: string): void {
    if (dialog === 'subscriptionReactivate') {
      OptimizelyService.trackEvent('checkout_renewal_cta_clicked');
    }
    this.isDialogShown[dialog] = true;
  }

  hideDialog(name: string): void {
    this.isDialogShown[name] = false;
  }

  async loadSubscriptionPlan(tryAgain?: boolean): Promise<void> {
    if (tryAgain) {
      this.errorSubscription = false;
      this.tryAgainSubscription = true;
    }

    // Reload Subscription
    if (this.activeSubscription.id == 0 || tryAgain) {
      await this.$store.dispatch('subscription/loadSubscription');
      this.errorSubscription = false;
      this.tryAgainSubscription = false;
    }

    // Validate reloaded subscription
    if (this.activeSubscription.id == 0) {
      this.errorSubscription = true;
      this.tryAgainSubscription = false;
    }

    if (this.invoices) {
      this.errorSubscription = false;
    }
  }

  onRenewSubscription(plan: Plan): void {
    this.$store.dispatch('subscription/setSubscriptionPlan', plan).then(() => {
      this.$router.push({ name: 'checkout_payment_form', params: { planId: plan.id } });
    });
  }

  async created(): Promise<void> {
    await this.stripeSubscriptionService.initSubscription();
    await this.loadSubscriptionPlan();
  }

  updatePaymentAddress(): void {
    this.openDialog('addressEdit');
    this.isPaymentMode = true;
  }

  isUserOnAbtestVariantENG1738(): boolean {
    return OptimizelyService.isAbTestVariantActive('24291960077', '24272590038');
  }
}
